import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import Button from '../ui/Button';
import { Dialog } from '../nursery/accounts/AccountBits';
import purchaseOrderService from '../../services/purchaseOrders';
import { errorMessage } from '../../utils/apiResponse';
import type { XeroSupplierCandidate } from '../../types/purchaseOrders';

/**
 * Accounts → Suppliers → Import from Xero (2026-10-09).
 *
 * Lists the supplier contacts in the organisation's Xero (Xero counts a
 * contact as a supplier once it has had a bill) and puts the ticked ones on
 * the list, each linked to its contact, so orders to them go to that contact
 * and never make a near-duplicate. A supplier already on the list under the
 * same name is linked rather than copied; one already linked is shown and
 * left alone. Any accounts seat, as for adding a supplier by hand.
 */

const checkCls = 'mt-0.5 h-4 w-4 shrink-0';

export default function XeroSupplierImport({ onClose, onImported }: {
  onClose: () => void;
  /** Called with a sentence saying what was done. */
  onImported: (summary: string) => void;
}) {
  const [candidates, setCandidates] = useState<XeroSupplierCandidate[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await purchaseOrderService.listXeroSuppliers();
        if (!live) return;
        if (res.success && res.data) {
          setCandidates(res.data);
          setChosen(new Set(res.data.filter((c) => c.on_list !== 'linked').map((c) => c.contact_id)));
        } else {
          setError(errorMessage(res, 'Could not read the suppliers from Xero'));
        }
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not read the suppliers from Xero'));
      }
    })();
    return () => { live = false; };
  }, []);

  const q = filter.trim().toLowerCase();
  const shown = useMemo(
    () => (candidates ?? []).filter((c) => !q || String(c.name ?? '').toLowerCase().includes(q)),
    [candidates, q],
  );
  const open = (candidates ?? []).filter((c) => c.on_list !== 'linked');

  const toggle = (id: string) => setChosen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const importChosen = async () => {
    setImporting(true);
    setError(null);
    try {
      const res = await purchaseOrderService.importXeroSuppliers([...chosen]);
      if (res.success && res.data) {
        const { created, linked } = res.data;
        const parts = [
          created > 0 && `${created} supplier${created === 1 ? '' : 's'} added from Xero`,
          linked > 0 && `${linked} already on your list linked to ${linked === 1 ? 'its' : 'their'} Xero contact`,
        ].filter(Boolean);
        onImported(parts.length ? `${parts.join(', and ')}.` : 'Nothing new to add from Xero.');
      } else {
        setError(errorMessage(res, 'Could not import the suppliers'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not import the suppliers'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog
      title="Import suppliers from Xero"
      subtitle="The contacts Xero holds as suppliers"
      onClose={() => { if (!importing) onClose(); }}
      wide
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={importing}>Cancel</Button>
          <Button onClick={importChosen} disabled={importing || chosen.size === 0}>
            {importing ? 'Importing…' : `Import ${chosen.size}`}
          </Button>
        </>
      )}
    >
      <p className="text-sm text-neutral-700 break-words">
        Each one you tick goes on your supplier list linked to its Xero contact, so purchase orders to it go to that
        contact and never make another. Xero lists a contact as a supplier once it has had a bill. Add an email to each
        afterwards to send orders from here.
      </p>

      {error && <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">{error}</p>}

      {candidates === null && !error && <p className="text-sm text-neutral-500">Reading Xero…</p>}

      {candidates !== null && candidates.length === 0 && (
        <p className="text-sm text-neutral-600 break-words">Xero has no suppliers yet: none of its contacts has had a bill.</p>
      )}

      {candidates !== null && candidates.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 border border-neutral-300 rounded-lg px-3 py-2 bg-white flex-1 basis-48 min-w-0">
              <Search className="h-4 w-4 text-neutral-400 shrink-0" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by name"
                aria-label="Filter Xero suppliers by name"
                className="flex-1 min-w-0 text-sm focus:outline-none bg-transparent"
              />
            </div>
            <button type="button" className="text-sm text-primary-600 underline"
              onClick={() => setChosen(new Set(open.map((c) => c.contact_id)))}>Tick all</button>
            <button type="button" className="text-sm text-primary-600 underline" onClick={() => setChosen(new Set())}>Tick none</button>
          </div>
          <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
            {shown.map((c) => {
              const linked = c.on_list === 'linked';
              return (
                <li key={c.contact_id}>
                  <label className={`flex items-start gap-3 px-3 py-2 ${linked ? 'opacity-70' : 'cursor-pointer hover:bg-neutral-50'}`}>
                    <input type="checkbox" className={checkCls} disabled={linked}
                      checked={!linked && chosen.has(c.contact_id)} onChange={() => toggle(c.contact_id)} />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-neutral-900 break-words">{c.name}</span>
                      {(c.contact_email || c.city) && (
                        <span className="block text-xs text-neutral-500 break-all">
                          {[c.contact_email, [c.city, c.state].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {linked && <span className="block text-xs text-neutral-500 break-words">Already on your list{c.supplier_name && c.supplier_name !== c.name ? ` as ${c.supplier_name}` : ''}</span>}
                      {c.on_list === 'same_name' && (
                        <span className="block text-xs text-indigo-700 break-words">On your list already: importing links it to this contact</span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
            {shown.length === 0 && <li className="px-3 py-2 text-sm text-neutral-500">No supplier in Xero matches &ldquo;{filter}&rdquo;.</li>}
          </ul>
        </>
      )}
    </Dialog>
  );
}
